import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Compact multi-select with search (правка №4, Чат 1). [Claude] 2026-11-03
 * options: [{ value, label, group?, hint? }]; selected: [values as strings].
 */
export default function MultiSelectDropdown({
    label, options, selected, onChange, placeholder = "Поиск…", loading = false, testId, emptyText = "Ничего не найдено",
    footer = null, width = "w-80",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef(null);
    const chosen = useMemo(() => new Set((selected || []).map(String)), [selected]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const needle = query.trim().toLocaleLowerCase("ru");
    const visible = (options || []).filter((o) => !needle || String(o.label).toLocaleLowerCase("ru").includes(needle));
    const groups = [];
    for (const option of visible) {
        const key = option.group || "";
        let group = groups.find((g) => g.key === key);
        if (!group) {
            group = { key, items: [] };
            groups.push(group);
        }
        group.items.push(option);
    }
    const toggle = (value) => {
        const key = String(value);
        const next = chosen.has(key) ? [...chosen].filter((v) => v !== key) : [...chosen, key];
        onChange(next);
    };
    const count = chosen.size;

    return <div className="relative" ref={ref} data-testid={testId}>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
            className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors ${count ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
            {label}{count ? <span className="rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">{count}</span> : null}
            <svg className="h-3.5 w-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {open ? <div className={`absolute left-0 z-40 mt-1 ${width} max-w-[90vw] rounded-xl border border-slate-200 bg-white p-2 shadow-xl`}>
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder}
                className="mb-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-sm outline-none focus:border-amber-400 focus:bg-white" />
            <div className="max-h-72 overflow-y-auto">
                {loading ? <p className="px-2 py-3 text-sm text-slate-400">Загрузка…</p> : null}
                {!loading && !visible.length ? <p className="px-2 py-3 text-sm text-slate-400">{emptyText}</p> : null}
                {groups.map((group) => <div key={group.key || "_"}>
                    {group.key ? <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group.key}</div> : null}
                    {group.items.map((option) => <label key={String(option.value)}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        <input type="checkbox" checked={chosen.has(String(option.value))} onChange={() => toggle(option.value)} className="h-4 w-4 accent-amber-500" />
                        <span className="min-w-0 flex-1 truncate" title={option.label}>{option.label}</span>
                        {option.hint ? <span className="shrink-0 text-xs text-slate-400">{option.hint}</span> : null}
                    </label>)}
                </div>)}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                <button type="button" disabled={!count} onClick={() => onChange([])} className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40">Очистить</button>
                {footer}
                <button type="button" onClick={() => setOpen(false)} className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800">Готово</button>
            </div>
        </div> : null}
    </div>;
}
